import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Divider, Typography } from 'antd';
import { TbWorldOff } from 'react-icons/tb';

import { useBillingSnapshot } from '@/hooks/useBillingSnapshot';
import { workspaceService } from '@/services/database';
import { LearnMoreFooter, PlanTag, SettingsToggleRow, SmallTooltip } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { hasGatedFeatureAccess } from '@/shared/entitlements';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { MemberPermissionType, Workspace } from '@/types';

import WorkspaceOperationalPreferences, {
    WorkspaceOperationalPreferenceKey,
    WorkspaceOperationalPreferenceUpdate,
} from './components/WorkspaceOperationalPreferences';

const Preferences: FC = ({}): JSX.Element => {
    const { selectedUser: user } = useUserStore();
    const { applyWorkspaceDelta, selectedWorkspace: workspace } = useWorkspaceStore();
    const { snapshot: billingSnapshot } = useBillingSnapshot();

    const [removeBranding, setRemoveBranding] = useState(Boolean(workspace?.removeBranding));
    const [saving, setSaving] = useState(false);
    const [savingPreference, setSavingPreference] = useState<WorkspaceOperationalPreferenceKey>();

    const memberPermission = user?.activeMember?.permission;
    const canManageBranding =
        memberPermission === MemberPermissionType.OWNER || memberPermission === MemberPermissionType.ADMIN;
    const hasBrandingRemovalPlan = hasGatedFeatureAccess(billingSnapshot, 'brandingRemoval');
    const disabledReason = !canManageBranding
        ? 'Only workspace owners and admins can change public-resource branding.'
        : !hasBrandingRemovalPlan
          ? 'Upgrade your plan to remove branding from publicly shared resources.'
          : undefined;
    const operationalPreferencesDisabledReason = !canManageBranding
        ? 'Only workspace owners and admins can change workspace-wide preferences.'
        : undefined;

    useEffect(() => {
        setRemoveBranding(Boolean(workspace?.removeBranding));
    }, [workspace?.id, workspace?.removeBranding]);

    const applyWorkspacePreference = (workspaceId: string, payload: Partial<Workspace>) => {
        applyWorkspaceDelta({
            v: 1,
            type: 'delta:packet',
            entity: 'workspace',
            operation: 'update',
            entityId: workspaceId,
            payload,
            timestamp: Date.now(),
        });
    };

    const handleRemoveBrandingToggle = async (c: boolean) => {
        if (!workspace?.id || saving || disabledReason) {
            return;
        }

        const workspaceId = workspace.id;
        const previousValue = removeBranding;

        setRemoveBranding(c);
        setSaving(true);
        applyWorkspacePreference(workspaceId, { removeBranding: c });

        try {
            const updatedWorkspace = await workspaceService.setBrandingRemoval(workspaceId, c);

            setRemoveBranding(updatedWorkspace.removeBranding);
            applyWorkspacePreference(workspaceId, updatedWorkspace);

            toast.success(`Branding ${updatedWorkspace.removeBranding ? 'removed' : 're-enabled'}`);
        } catch (error) {
            setRemoveBranding(previousValue);
            applyWorkspacePreference(workspaceId, { removeBranding: previousValue });
            captureException(error, {
                extra: {
                    workspaceId,
                    removeBranding: c,
                },
            });
            toast.error('Unable to update public-resource branding');
        } finally {
            setSaving(false);
        }
    };

    const getPreferencePayload = (update: WorkspaceOperationalPreferenceUpdate): Partial<Workspace> => {
        switch (update.key) {
            case 'dateFormat':
                return { dateFormat: update.value };
            case 'financialValueDisplay':
                return { financialValueDisplay: update.value };
            case 'fiscalYearStartMonth':
                return { fiscalYearStartMonth: update.value };
            case 'reportingCurrency':
                return { reportingCurrency: update.value };
            case 'weekStartDay':
                return { weekStartDay: update.value };
            case 'workspaceTimeZone':
                return { workspaceTimeZone: update.value };
        }
    };

    const getCurrentPreferencePayload = (
        currentWorkspace: Workspace,
        key: WorkspaceOperationalPreferenceKey,
    ): Partial<Workspace> => {
        switch (key) {
            case 'dateFormat':
                return { dateFormat: currentWorkspace.dateFormat ?? 'MM/DD/YYYY' };
            case 'financialValueDisplay':
                return { financialValueDisplay: currentWorkspace.financialValueDisplay };
            case 'fiscalYearStartMonth':
                return { fiscalYearStartMonth: currentWorkspace.fiscalYearStartMonth ?? 1 };
            case 'reportingCurrency':
                return { reportingCurrency: currentWorkspace.reportingCurrency ?? 'USD' };
            case 'weekStartDay':
                return { weekStartDay: currentWorkspace.weekStartDay ?? 'monday' };
            case 'workspaceTimeZone':
                return { workspaceTimeZone: currentWorkspace.workspaceTimeZone ?? 'UTC' };
        }
    };

    const handleOperationalPreferenceChange = async (update: WorkspaceOperationalPreferenceUpdate) => {
        if (!workspace?.id || savingPreference || operationalPreferencesDisabledReason) {
            return;
        }

        const workspaceId = workspace.id;
        const optimisticPayload = getPreferencePayload(update);
        const rollbackPayload = getCurrentPreferencePayload(workspace, update.key);

        setSavingPreference(update.key);
        applyWorkspacePreference(workspaceId, optimisticPayload);

        try {
            const updatedWorkspace = await workspaceService.update(workspaceId, optimisticPayload);

            applyWorkspacePreference(workspaceId, updatedWorkspace);
            toast.success('Workspace preference updated');
        } catch (error) {
            applyWorkspacePreference(workspaceId, rollbackPayload);
            captureException(error, {
                extra: {
                    preference: update.key,
                    workspaceId,
                },
            });
            toast.error('Unable to update workspace preference');
        } finally {
            setSavingPreference(undefined);
        }
    };

    return (
        <>
            <section aria-labelledby='workspace-wide-preferences-heading'>
                <Typography.Title id='workspace-wide-preferences-heading' level={4} style={{ marginBottom: '0.25rem' }}>
                    Preferences
                </Typography.Title>
                <Typography.Paragraph type='secondary' style={{ marginBottom: '1rem' }}>
                    {operationalPreferencesDisabledReason
                        ? 'Choose the calendar, reporting, and public-sharing defaults used throughout this workspace. Only workspace owners and admins can make changes.'
                        : 'Choose the calendar, reporting, and public-sharing defaults used throughout this workspace.'}
                </Typography.Paragraph>
            </section>
            {workspace ? (
                <WorkspaceOperationalPreferences
                    additionalPreference={
                        <SmallTooltip disabled={!disabledReason} placement='left' title={disabledReason}>
                            <div>
                                <SettingsToggleRow
                                    checked={removeBranding}
                                    description="Hide the 'Made with Countercyclical' attribution on publicly shared workspace resources."
                                    disabled={Boolean(disabledReason) || saving}
                                    icon={<TbWorldOff size='1rem' />}
                                    label={
                                        <>
                                            Remove Branding
                                            <PlanTag
                                                plan='pro'
                                                style={{ marginLeft: '0.75rem', verticalAlign: '0.1rem' }}
                                            />
                                        </>
                                    }
                                    loading={saving}
                                    switchAriaLabel='Remove branding from publicly shared resources'
                                    switchSize='default'
                                    onChange={(checked) => {
                                        void handleRemoveBrandingToggle(checked);
                                    }}
                                />
                            </div>
                        </SmallTooltip>
                    }
                    disabled={Boolean(operationalPreferencesDisabledReason)}
                    savingPreference={savingPreference}
                    workspace={workspace}
                    onChange={handleOperationalPreferenceChange}
                />
            ) : null}
            <footer aria-label='Workspace preferences documentation' style={{ paddingBottom: '1rem' }}>
                <Divider style={{ margin: '1.5rem 0 1rem' }} />
                <LearnMoreFooter align='center' topic='workspace preferences' />
            </footer>
        </>
    );
};

export default Preferences;
